import {
  faAngleLeft,
  faAngleRight,
  faArchive,
  faRedo,
  faStar,
  faTag,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import Button from '../../../components/base/Button';
import IndeterminateCheckbox from '../../../components/base/IndeterminateCheckbox';
import TooltipIconButton from '../../../components/common/TooltipIconButton';
import { useBulkSelect } from '../../../providers/BulkSelectProvider';

interface InboxToolbarProps {
  size?: 'sm' | 'lg';
  className?: string;
  // 🔧 NUEVAS PROPS PARA DATOS REALES
  emails?: any[];
  loading?: boolean;
  totalCount?: number;
  currentPage?: number;
  itemsPerPage?: number;
  lastUpdate?: Date | null;
  onRefresh?: () => void;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
}

const InboxToolbar = ({ 
  size = 'lg', 
  className,
  // 🔧 VALORES POR DEFECTO
  emails = [],
  loading = false,
  totalCount = 0,
  currentPage = 1,
  itemsPerPage = 50,
  lastUpdate = null,
  onRefresh,
  onPreviousPage,
  onNextPage
}: InboxToolbarProps) => {
  const { getParentCheckboxProps } = useBulkSelect();

  // 🔧 CALCULAR RANGOS REALES
  const emailsCount = emails.length;
  const startItem = emailsCount > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0;
  const endItem = Math.min(startItem + emailsCount - 1, totalCount);
  const displayTotal = totalCount > 0 ? totalCount : emailsCount;

  // 🔧 FORMATO DE FECHA DE ÚLTIMA ACTUALIZACIÓN
  const formatLastUpdate = (): string => {
    if (!lastUpdate) return 'Nunca actualizado';
    
    const now = new Date();
    const diff = now.getTime() - lastUpdate.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Actualizado ahora';
    if (minutes === 1) return 'Actualizado hace 1m';
    if (minutes < 60) return `Actualizado hace ${minutes}m`;
    
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return 'Actualizado hace 1h';
    if (hours < 24) return `Actualizado hace ${hours}h`;
    
    return `Actualizado ${lastUpdate.toLocaleDateString()}`;
  };

  // 🔧 MANEJAR REFRESH
  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    } else {
      location.reload();
    }
  };

  // 🔧 LÓGICA DE PAGINACIÓN
  const canGoPrevious = currentPage > 1;
  const canGoNext = endItem < displayTotal;

  return (
    <>
      <div
        className={classNames(
          className,
          'd-flex align-items-center flex-wrap position-sticky pb-2 bg-body z-2 email-toolbar'
        )}
      >
        <div className="d-flex align-items-center flex-1 me-2">
          <Button 
            className="p-0 me-2" 
            onClick={handleRefresh}
            disabled={loading}
            title="Actualizar emails"
          >
            <FontAwesomeIcon 
              icon={faRedo} 
              className={`text-primary fs-10 ${loading ? 'fa-spin' : ''}`} 
            />
          </Button>
          <p className="fw-semibold fs-10 text-body-tertiary text-opacity-85 mb-0 lh-sm text-nowrap">
            {formatLastUpdate()}
          </p>
        </div>
        <div className="d-flex gap-3">
          {/* 🔧 MOSTRAR CONTADOR SOLO SI HAY EMAILS */}
          {emailsCount > 0 && (
            <p className="text-body-tertiary text-opacity-85 fs-9 fw-semibold mb-0">
              {size === 'lg' ? 'Showing : ' : ' '}
              <span className="text-body">{startItem}-{endItem}</span>
              {' of '}
              <span className="text-body">{displayTotal}</span>
            </p>
          )}
          
          {/* 🔧 BOTONES DE PAGINACIÓN */}
          <Button 
            className="p-0" 
            type="button"
            onClick={onPreviousPage}
            disabled={!canGoPrevious || loading}
            title="Página anterior"
          >
            <FontAwesomeIcon
              icon={faAngleLeft}
              className={`fs-10 ${canGoPrevious ? 'text-primary' : 'text-body-quaternary'}`}
            />
          </Button>
          <Button 
            className="p-0" 
            type="button"
            onClick={onNextPage}
            disabled={!canGoNext || loading}
            title="Página siguiente"
          >
            <FontAwesomeIcon
              icon={faAngleRight}
              className={`fs-10 ${canGoNext ? 'text-primary' : 'text-body-quaternary'}`}
            />
          </Button>
        </div>
      </div>
      <div className="border-y border-translucent py-2 d-flex justify-content-between">
        <IndeterminateCheckbox {...getParentCheckboxProps()} />
        <div className="d-flex gap-2">
          <TooltipIconButton
            iconClass="fs-10"
            title="Archive"
            icon={faArchive}
          />
          <TooltipIconButton iconClass="fs-10" title="Delete" icon={faTrash} />
          <TooltipIconButton iconClass="fs-10" title="Star" icon={faStar} />
          <TooltipIconButton iconClass="fs-10" title="Tags" icon={faTag} />
        </div>
      </div>
    </>
  );
};

export default InboxToolbar;